import React, { useState, useEffect } from "react";
import api from "../../../services/AxiosConnection";
import { useNotification } from "../../../context/NotificationContext";
import { Vortex } from "react-loader-spinner";
import useAuth from "../../../hooks/useAuth";

const FormSection = ({ title, fields, isEditing, isSuperAdmin }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
      <h3 className="text-lg font-semibold text-blue-800 mb-4">{title}</h3>
      <div className="space-y-4">
        {fields.map((field) => (
          <div
            key={field.id}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center"
          >
            <label className="text-xs text-gray-700 font-medium">
              {field.label}:
            </label>
            {/* If the field has a dropdown, render select, otherwise render input */}
            {field.options ? (
              <select
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                disabled={!isEditing || !isSuperAdmin}
                className="col-span-2 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                          disabled:bg-gray-50 disabled:text-gray-500 text-xs"
              >
                {field.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                disabled={!isEditing || !isSuperAdmin}
                className="col-span-2 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                          disabled:bg-gray-50 disabled:text-gray-500 text-xs"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SystemConfigForm = () => {
  const { user, selectedSite } = useAuth();
  const isSuperAdmin = user?.role === "Super Admin";
  const [formData, setFormData] = useState({
    SiteId: "",
    SiteCode: "",
    MpPanelisinstalledon: "",
    GoogleLocation: "",
    SiteLocation: "",
    SelectProxysenforDirection: "",
    Direction: "",
    Noofproxysens: "",
    Nooftempsens: "",
    DiffbetwnproxysensS_C: "",
    DiffbetwnproxysensS_E: "",
    Description: "",
  });
  const [existingId, setExistingId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const { triggerNotification } = useNotification();

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!selectedSite) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(
          `/control-panel/getSystemConfiguration?siteDb=${selectedSite}`
        );

        if (isMounted) {
          if (response.data && response.data.data) {
            const apiData = response.data.data;
            setFormData({
              SiteId: apiData.SiteId || "",
              SiteCode: apiData.SiteCode || "",
              MpPanelisinstalledon: apiData.MpPanelisinstalledon || "",
              GoogleLocation: apiData.GoogleLocation || "",
              SiteLocation: apiData.SiteLocation || "",
              SelectProxysenforDirection: apiData.SelectProxysenforDirection || "",
              Direction: apiData.Direction || "",
              Noofproxysens: apiData.Noofproxysens || "",
              Nooftempsens: apiData.Nooftempsens || "",
              DiffbetwnproxysensS_C: apiData.DiffbetwnproxysensS_C || "",
              DiffbetwnproxysensS_E: apiData.DiffbetwnproxysensS_E || "",
              Description: apiData.Description || "",
            });
            setExistingId(response.data.data.ID);
            triggerNotification(
              "Site Configuration Details Fetched Successfully!",
              "success"
            );
          } else {
            setFormData({
              SiteId: "",
              SiteCode: "",
              MpPanelisinstalledon: "",
              GoogleLocation: "",
              SiteLocation: "",
              SelectProxysenforDirection: "",
              Direction: "",
              Noofproxysens: "",
              Nooftempsens: "",
              DiffbetwnproxysensS_C: "",
              DiffbetwnproxysensS_E: "",
              Description: "",
            });
            setExistingId(null);
            if (isSuperAdmin) {
              setIsEditing(true); // Allow Super Admins to create new config
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching data:", error);
          triggerNotification("Failed to fetch configuration", "error");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [selectedSite, isSuperAdmin]); // Only depend on selectedSite and isSuperAdmin

  const handleFieldChange = (field, value) => {
    if (!isSuperAdmin) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!isSuperAdmin) return;
    try {
      setLoading(true);
      const payload = {
        siteDb: selectedSite,
        ...formData,
        ...(existingId && { ID: existingId }),
      };

      const method = existingId ? "put" : "post";
      const url = existingId
        ? "/control-panel/updateSystemConfiguration"
        : "/control-panel/insertSystemConfiguration";

      const response = await api[method](url, payload);
      if (response.data?.ID) {
        setExistingId(response.data.ID);
      }
      setIsEditing(false);

      triggerNotification(
        `Site Configuration ${existingId ? "Updated" : "Created"} Successfully!`,
        "success"
      );
    } catch (error) {
      console.error("Error saving data:", error);
      const errorMessage =
        error?.response?.data?.message || "An unexpected error occurred.";
      triggerNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {loading && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
         <Vortex
           visible={true}
           height={80}
           width={80}
           ariaLabel="vortex-loading"
           wrapperStyle={{}}
           wrapperClass="vortex-wrapper"
           colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
         />
       </div>
      )}

      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <h2 className="text-lg font-semibold text-blue-800 mb-2">
          System Configuration Settings
        </h2>
        <p className="text-xs text-gray-600">
          {existingId
            ? isSuperAdmin
              ? "Edit existing configuration"
              : "View existing configuration"
            : isSuperAdmin
            ? "Create new configuration"
            : "No configuration available"}
        </p>
      </div>

      {(!existingId && !isSuperAdmin) ? (
        <div className="text-center text-gray-500 text-sm">
          No system configuration available.
        </div>
      ) : (
        <>
          {/* Site Details Section */}
          <FormSection
            title="Site Details"
            isEditing={isEditing && isSuperAdmin}
            isSuperAdmin={isSuperAdmin}
            fields={[
              {
                id: "SiteId",
                label: "Site Id",
                value: formData.SiteId,
                onChange: (v) => handleFieldChange("SiteId", v),
              },
              {
                id: "SiteCode",
                label: "Site Code",
                value: formData.SiteCode,
                onChange: (v) => handleFieldChange("SiteCode", v),
              },
              {
                id: "MpPanelisinstalledon",
                label: "Mp Panel is installed on (LH/RH)",
                value: formData.MpPanelisinstalledon,
                onChange: (v) => handleFieldChange("MpPanelisinstalledon", v),
                options: ["LH", "RH"],
              },
            ]}
          />

          {/* Location Details Section */}
          <FormSection
            title="Location Details"
            isEditing={isEditing && isSuperAdmin}
            isSuperAdmin={isSuperAdmin}
            fields={[
              {
                id: "GoogleLocation",
                label: "Google Location",
                value: formData.GoogleLocation,
                onChange: (v) => handleFieldChange("GoogleLocation", v),
              },
              {
                id: "SiteLocation",
                label: "Site Location",
                value: formData.SiteLocation,
                onChange: (v) => handleFieldChange("SiteLocation", v),
              },
              {
                id: "SelectProxysenforDirection",
                label: "Select Proxy sen. for direction (S/C)",
                value: formData.SelectProxysenforDirection,
                onChange: (v) => handleFieldChange("SelectProxysenforDirection", v),
                options: ["S", "C"],
              },
              {
                id: "Direction",
                label: "Direction (UP/DOWN)",
                value: formData.Direction,
                onChange: (v) => handleFieldChange("Direction", v),
                options: ["UP", "DOWN"],
              },
            ]}
          />

          {/* Sensor Configuration Section */}
          <FormSection
            title="Sensor Configuration"
            isEditing={isEditing && isSuperAdmin}
            isSuperAdmin={isSuperAdmin}
            fields={[
              {
                id: "Noofproxysens",
                label: "Number of Proxy Sensors",
                value: formData.Noofproxysens,
                onChange: (v) => handleFieldChange("Noofproxysens", v),
              },
              {
                id: "Nooftempsens",
                label: "Number of Temp Sensors",
                value: formData.Nooftempsens,
                onChange: (v) => handleFieldChange("Nooftempsens", v),
              },
              {
                id: "DiffbetwnproxysensS_C",
                label: "Diff betwn proxy sens (S&C)",
                value: formData.DiffbetwnproxysensS_C,
                onChange: (v) => handleFieldChange("DiffbetwnproxysensS_C", v),
              },
              {
                id: "DiffbetwnproxysensS_E",
                label: "Diff betwn proxy sens (S&E)",
                value: formData.DiffbetwnproxysensS_E,
                onChange: (v) => handleFieldChange("DiffbetwnproxysensS_E", v),
              },
            ]}
          />
        </>
      )}

      {isSuperAdmin && (
        <div className="flex justify-center gap-4 mt-8 pt-4 border-t">
          {existingId && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Edit
            </button>
          )}

          {isEditing && (
            <>
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </>
          )}

          {!existingId && (
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Save Configuration
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SystemConfigForm;